import moment from 'moment';
import { GetApp } from '@material-ui/icons';
import { useCallback, useState } from 'react';
import { Button, Box } from '@material-ui/core';

import { useSnackbar } from 'src/lib/hooks';
import { ListTransactionsV2FilterInput, useExportTransactionsMutation } from 'src/@types';
import useAnalytics from 'src/lib/hooks/useAnalytics';
import { events } from 'src/lib/utils/analytics-events';
import { useDataContext } from 'src/components/shared/DataContext/useDataContext';
import ExportConfirmationModal from './ExportConfirmationModal';

type ExportButtonProps = {
  empty: boolean;
  filter: ListTransactionsV2FilterInput;
  disabled?: boolean;
  pageSize?: number;
  startDate: Date;
  endDate: Date;
};

export default function ExportButton({ empty, disabled, filter, startDate, endDate }: ExportButtonProps) {
  const [dataRef, setData] = useDataContext();
  const snackbar = useSnackbar();
  const { track } = useAnalytics();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalState, setModalState] = useState<{
    success: boolean;
    title: string;
    message: string;
  }>({
    success: false,
    title: '',
    message: '',
  });

  // Use the custom export transactions mutation hook
  const [exportTransactions, { loading }] = useExportTransactionsMutation();

  const onClick = useCallback(async () => {
    if (empty) {
      snackbar.error("There's no data to export.");
      return;
    }

    try {
      /**
       * Use the same date format as the table query filter (ISO timestamp strings).
       * The startDate and endDate are already UTC Date objects from useUTCDateRangeValue,
       * so we use toISOString() directly to match exactly what TransactionsList uses.
       * This ensures the export uses the exact same date range as the list query.
       */
      const fromDate: string = startDate.toISOString();
      let toDate: string = endDate.toISOString();

      // Validate that the date range doesn't exceed 6 months
      // Use UTC mode to ensure consistent date calculations regardless of timezone
      const fromMoment = moment.utc(startDate);
      const toMoment = moment.utc(endDate);
      const monthsDiff = toMoment.diff(fromMoment, 'months', true);

      if (monthsDiff > 6) {
        /**
         * If range exceeds 6 months, adjust toDate to be exactly 6 months from fromDate.
         * Set to end of day (23:59:59.999) to ensure all transactions from the last day
         * of the capped range are included in the export.
         * Use moment.utc() to ensure the calculation happens in UTC mode, preventing
         * timezone-related date shifts when adding months in timezones behind UTC.
         */
        const cappedDate = moment.utc(startDate).add(6, 'months').toDate();
        const year = cappedDate.getUTCFullYear();
        const month = String(cappedDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(cappedDate.getUTCDate()).padStart(2, '0');
        toDate = `${year}-${month}-${day}T23:59:59.999Z`;
      }

      const siteIds = filter.siteId?.in;
      await exportTransactions({
        variables: {
          input: {
            fromDate,
            toDate,
            // omit from input if none are selected
            ...(siteIds?.length && { siteIds }),
          },
        },
      });
      // Show success modal
      setModalState({
        success: true,
        title: 'Request submitted successfully',
        message: 'An email with a link to your file should arrive within 15 minutes.',
      });
      setModalOpen(true);
      track(events.reportsView.DOWNLOAD_SESSIONS_CSV);
    } catch (error: unknown) {
      // Show error modal with more specific messaging
      let errorMessage = 'We encountered an issue with your request, please try again.';

      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        const graphQLError = (error as { graphQLErrors: Array<{ message: string }> }).graphQLErrors[0];
        if (graphQLError?.message?.includes('toDate must be within 6 months')) {
          errorMessage = 'The date range for export cannot exceed 6 months. Please adjust your filters and try again.';
        }
      }

      setModalState({
        success: false,
        title: 'Request not successful',
        message: errorMessage,
      });
      setModalOpen(true);
    }
  }, [empty, startDate, endDate, filter.siteId?.in, exportTransactions, snackbar, track]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        onClick={onClick}
        disabled={loading || disabled}
        ref={dataRef}
        {...setData('export-button')}
      >
        <GetApp />
        <Box component="span" pl={2}>
          Download CSV
        </Box>
      </Button>

      <ExportConfirmationModal
        open={modalOpen}
        onClose={handleCloseModal}
        title={modalState.title}
        message={modalState.message}
      />
    </>
  );
}
